import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { TranslateService } from '@ngx-translate/core';
import { getNewDynamicFormFormObject } from './utils/dynamic-form.form-object';
import { DynamicFormRepository } from '@/store/dynamic-form-state/dynamic-form.repository';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { dynamicFieldStoreFactory } from '@/store/dynamic-form-state/dynamic-field-state/dynamic-field.store';
import { DynamicFieldsService } from '../dynamic-fields.service';
import { DynamicForm, SelectOption } from '@/components/form-builder/form-builder.types';
import { BehaviorSubject } from 'rxjs';
import { DynamicFormsService } from '../dynamic-forms.service';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { DynamicFormDto } from '@/types';
import { DynamicFormsWorkflowBlocksService } from '../dynamic-forms-workflow-blocks.service';
import { DynamicBlocks } from '@/components/block-builder/block-builder.types';
import { BlockBuilderComponent } from '@/components/block-builder/block-builder.component';
import { dynamicFormSchema } from '@/validations/dynamic-form.validation';
import { DocSubmissionService } from '../../doc-submission/doc-submission.service';

@Component({
  selector: 'sm-new-dynamic-form',
  imports: [PageHeaderComponent, FormBuilderComponent, BlockBuilderComponent],
  templateUrl: './new-dynamic-form.component.html',
  styleUrl: './new-dynamic-form.component.scss',
})
export class NewDynamicFormComponent implements OnInit, OnDestroy {
  dynmicFormRepository = inject(DynamicFormRepository);
  multiFormAccordionService = inject(MultiFormAccordionService);
  docSubmissionService = inject(DocSubmissionService);
  dynamicFieldsService = inject(DynamicFieldsService);
  dynamicFormService = inject(DynamicFormsService);
  messageService = inject(MessageService);
  router = inject(Router);
  translate = inject(TranslateService);

  dynamicFormWorkflowBlocksService = inject(DynamicFormsWorkflowBlocksService);
  controlBlocks: DynamicBlocks = this.dynamicFormWorkflowBlocksService.getPreDraftBlocks({
    save: () => this.addDynamicForm(),
    rest: () => {
      this.dynmicFormRepository.reset();
      this.dynamicFieldsService.resetAllDynamicFields();
    },
  });

  breadcrumb = [
    {
      label: 'dac.title',
      routerLink: ['/settings', 'dynamic-forms'],
    },
    {
      label: 'transanctionalForm.pages.new',
      routerLink: ['/settings', 'dynamic-forms', 'new'],
    },
  ];

  loading$ = new BehaviorSubject<boolean>(false);
  title: string = this.translate.instant('transanctionalForm.pages.new');

  folderTypeOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject<SelectOption[]>([]);

  formObject: DynamicForm = getNewDynamicFormFormObject({
    store: this.dynmicFormRepository,
    folderTypeOptions: this.folderTypeOptions,
    resetFormType: () => this.dynamicFieldsService.restFormType(),
  });

  constructor() {
    this.multiFormAccordionService.formFactory = (id: string) => dynamicFieldStoreFactory(id);
    this.multiFormAccordionService.init();
  }

  ngOnInit() {
    this.dynamicFieldsService.initialize();
    this.docSubmissionService.getAllFolderTypes().subscribe({
      next: data => {
        this.folderTypeOptions.next(
          data.map(typeDossier => ({
            name: typeDossier.libelle,
            code: typeDossier.id,
          }))
        );
      },
      error: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: error.error.message,
        });
      },
    });
  }

  ngOnDestroy() {
    this.multiFormAccordionService.reset(0);
  }

  addDynamicForm() {
    const data = this.dynmicFormRepository.get<DynamicFormDto>('dto');
    const fieldsValidation = this.dynamicFieldsService.validateAllFieldsDtos();
    const result = dynamicFormSchema.safeParse(data);

    if (result.success && fieldsValidation) {
      data.fields = this.dynamicFieldsService.getAllDynamicFieldsDtos();
      this.loading$.next(true);
      this.dynamicFormService.addDynamicForm(data).subscribe({
        next: () => {
          this.multiFormAccordionService.reset(0);
          this.dynmicFormRepository.reset();
          this.router.navigate(['/settings', 'dynamic-forms']);
          this.messageService.add({
            severity: 'success',
            summary: 'Succès',
            detail: 'Le formulaire dynamique a été ajouté avec succès',
          });
          this.loading$.next(false);
        },
        error: error => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: error.error.message,
          });
          this.loading$.next(false);
        },
      });
    } else if (result.error) {
      this.dynmicFormRepository.setErrors(result.error.flatten().fieldErrors);
    }
  }
}
